import { motion } from "framer-motion";

const SKILLS = [
  {
    category: "AI & Machine Learning",
    color: "var(--accent)",
    items: [
      "Agentic RAG",
      "LLM Fine-tuning",
      "Prompt Engineering",
      "LangChain / LangGraph",
      "Vector Search",
      "NLP & Text Embeddings",
      "Reinforcement Learning",
      "Computer Vision",
    ],
  },
  {
    category: "Engineering & Data",
    color: "var(--accent2)",
    items: [
      "Python",
      "FastAPI",
      "Docker",
      "Apache Airflow",
      "PostgreSQL",
      "REST APIs & SSE",
      "Data Pipelines",
      "Git",
    ],
  },
  {
    category: "Product & Delivery",
    color: "var(--accent3)",
    items: [
      "Product Management",
      "Agile / Scrum",
      "Stakeholder Management",
      "User Research",
      "React / TypeScript",
      "Cloudflare & Vercel",
      "Technical Writing",
      "Cross-functional Leadership",
    ],
  },
];

const PROJECTS = [
  {
    name: "Arcane Illumina",
    tagline: "AI-powered arXiv research knowledge platform",
    description:
      "A production Agentic RAG system that lets you chat with 100+ arXiv papers. The RAG pipeline is fully agentic — guardrail → retrieve → grade → rewrite → stream — with hybrid vector + BM25 search, contextual chunk retrieval, and LLM-based relevance grading. Runs on Cloudflare Pages Functions with Pinecone, Groq inference, and Upstash Redis caching.",
    stack: [
      "Agentic RAG",
      "Cloudflare Workers",
      "Pinecone",
      "Groq",
      "React",
      "Upstash",
    ],
    accent: "var(--accent)",
    link: "/",
    linkLabel: "You're here →",
    internal: true,
  },
  {
    name: "Keyword Spotting — CNN on Spectrogram Features",
    tagline: "End-to-end ML pipeline for microcontroller deployment",
    description:
      "Built a keyword spotting pipeline: engineered STFT spectrogram features and trained a compact CNN classifier in TensorFlow. Exported and int8-quantized the model for microcontroller deployment using TensorFlow Lite Micro with representative calibration; evaluated accuracy and tuned thresholds for robust keyword vs. silence decisions.",
    stack: [
      "Python",
      "TensorFlow",
      "TensorFlow Lite Micro",
      "CNN",
      "Signal Processing",
    ],
    accent: "var(--accent2)",
    link: "https://github.com/koyava/keyword-spotting",
    linkLabel: "GitHub →",
    internal: false,
  },
  {
    name: "Walmart Sales Forecasting",
    tagline: "Time series forecasting with interactive Streamlit deployment",
    description:
      "Built an end-to-end forecasting system on Walmart weekly sales (2010–2012) using Python. Performed EDA, engineered features, trained Auto ARIMA and Holt-Winters models, evaluated with WMAE, and deployed interactive Streamlit apps for training and 4-week ahead predictions.",
    stack: ["Python", "ARIMA", "Holt-Winters", "Streamlit", "pandas"],
    accent: "var(--accent3)",
    link: "https://github.com/koyava/walmart-sales-forecasting",
    linkLabel: "GitHub →",
    internal: false,
  },
  {
    name: "Wine Quality & Classification Analysis",
    tagline: "Statistical modelling and classification in R",
    description:
      "Analyzed 6,497 Portuguese wines through EDA, t-tests, regression, and classification to identify key quality factors and predict wine type and quality. Applied factor analysis for dimensionality reduction and validated models using confusion matrix and AUC.",
    stack: ["R", "EDA", "Regression", "Classification", "Factor Analysis"],
    accent: "var(--accent)",
    link: "https://github.com/koyava/wine-dataset-analysis",
    linkLabel: "GitHub →",
    internal: false,
  },
];

const EDUCATION = [
  {
    degree: "M.Sc. Business Intelligence & Data Analytics",
    school: "Emden/Leer University of Applied Sciences, Germany",
    period: "Mar 2025 – Present",
    detail:
      "Practice-oriented master's focused on data analytics, machine learning, and business intelligence. Hands-on work with RAG systems, Agentic RAG pipelines, and LLM-based applications alongside Python, R, and SQL for data processing, visualisation, and predictive modelling. CGPA after first semester: 1.38.",
  },
  {
    degree: "B.Tech — Mechanical Engineering",
    school: "B.S.A Crescent Institute of Science & Technology, India",
    period: "Jun 2014 – Dec 2019",
    detail:
      "Core manufacturing systems, thermal engineering, and HVAC technologies. Final year project: portable thermoelectric refrigerator using the Peltier effect, integrating heat transfer, energy systems, and sustainable design.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, delay: i * 0.08 },
  }),
};

export default function BuiltBy() {
  return (
    <>
      {/* ── Hero ──────────────────────────────────────────────────────── */}
      <section className="bb-hero">
        <div className="container bb-hero__inner">
          <motion.div
            className="bb-avatar-wrap"
            initial={{ opacity: 0, scale: 0.88 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.55 }}
          >
            <img
              src="/avatar.jpg"
              alt="Adil Ibraheem Koyava"
              className="bb-avatar"
              onError={(e) => {
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "flex";
              }}
            />
            <div className="bb-avatar-fallback">AIK</div>
          </motion.div>

          <motion.div
            className="bb-hero__text"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <span className="badge bb-eyebrow">
              Builder · Researcher · Engineer
            </span>
            <h1 className="bb-name">
              Adil Ibraheem
              <br />
              <span className="text-gradient">Koyava</span>
            </h1>
            <p className="bb-role text-muted">
              Data Scientist · AI Engineer · Product Manager
            </p>
            <p className="bb-bio">
              M.Sc. Business Intelligence & Data Analytics at Emden/Leer
              University of Applied Sciences. 3+ years as Associate Product
              Manager across EdTech and Media — launching features, running A/B
              tests, and owning roadmaps. I now combine that product instinct
              with hands-on AI engineering: building Agentic RAG pipelines,
              fine-tuning LLMs, and shipping full-stack AI applications that
              solve real problems.
            </p>

            <div className="bb-hero-links">
              <a
                href="https://www.linkedin.com/in/adil-ibraheem-koyava/"
                target="_blank"
                rel="noreferrer"
                className="btn btn-primary"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
                LinkedIn
              </a>
              <a
                href="https://github.com/koyava"
                target="_blank"
                rel="noreferrer"
                className="btn btn-secondary"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 0C5.37 0 0 5.37 0 12c0 5.3 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 21.795 24 17.295 24 12c0-6.63-5.37-12-12-12" />
                </svg>
                GitHub
              </a>
              <span className="bb-location text-muted">
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 10c0 6-8 13-8 13s-8-7-8-13a8 8 0 0 1 16 0Z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                Hamburg, Germany
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Skills ────────────────────────────────────────────────────── */}
      <section className="section bb-skills">
        <div className="container">
          <div className="section-header">
            <h2>Skills & Technologies</h2>
            <span className="accent-line" />
          </div>

          <div className="bb-skills-grid">
            {SKILLS.map((group, gi) => (
              <motion.div
                key={group.category}
                className="bb-skill-group card"
                custom={gi}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.15 }}
              >
                <h3
                  className="bb-skill-group__title"
                  style={{ color: group.color }}
                >
                  {group.category}
                </h3>
                <div className="bb-skill-tags">
                  {group.items.map((item) => (
                    <span key={item} className="badge bb-skill-tag">
                      {item}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Projects ─────────────────────────────────────────────────── */}
      <section className="section bb-projects">
        <div className="container">
          <div className="section-header">
            <h2>Projects</h2>
            <span className="accent-line" />
          </div>

          <div className="bb-projects-list">
            {PROJECTS.map((p, i) => (
              <motion.div
                key={p.name}
                className="bb-project-card card"
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.15 }}
              >
                <div className="bb-project-card__top">
                  <div>
                    <h3 className="bb-project-name" style={{ color: p.accent }}>
                      {p.name}
                    </h3>
                    <p className="bb-project-tagline text-muted">{p.tagline}</p>
                  </div>
                  {p.internal ? (
                    <a href={p.link} className="bb-project-link">
                      {p.linkLabel}
                    </a>
                  ) : (
                    <a
                      href={p.link}
                      target="_blank"
                      rel="noreferrer"
                      className="bb-project-link"
                    >
                      {p.linkLabel}
                    </a>
                  )}
                </div>
                <p className="bb-project-desc text-muted">{p.description}</p>
                <div className="bb-project-stack">
                  {p.stack.map((s) => (
                    <span
                      key={s}
                      className="badge bb-stack-badge"
                      style={{ "--badge-accent": p.accent }}
                    >
                      {s}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Education ────────────────────────────────────────────────── */}
      <section className="section bb-edu">
        <div className="container">
          <div className="section-header">
            <h2>Education</h2>
            <span className="accent-line" />
          </div>

          <div className="bb-edu-list">
            {EDUCATION.map((e, i) => (
              <motion.div
                key={e.degree}
                className="bb-edu-item card"
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.2 }}
              >
                <div className="bb-edu-item__left">
                  <span className="bb-edu-period text-muted">{e.period}</span>
                </div>
                <div className="bb-edu-item__right">
                  <h3 className="bb-edu-degree">{e.degree}</h3>
                  <p className="bb-edu-school text-accent">{e.school}</p>
                  <p className="bb-edu-detail text-muted">{e.detail}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────────────── */}
      <section className="section bb-cta">
        <div className="container">
          <motion.div
            className="bb-cta-card card"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="bb-cta-title">Let's build something together</h2>
            <p className="bb-cta-sub text-muted">
              Open to collaborations, research opportunities, and interesting
              problems in AI engineering.
            </p>
            <div className="bb-cta-links">
              <a
                href="https://www.linkedin.com/in/adil-ibraheem-koyava/"
                target="_blank"
                rel="noreferrer"
                className="btn btn-primary"
              >
                Connect on LinkedIn
              </a>
              <a
                href="https://github.com/koyava"
                target="_blank"
                rel="noreferrer"
                className="btn btn-secondary"
              >
                View GitHub
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <style>{`
        /* ── Hero ── */
        .bb-hero {
          padding: calc(var(--nav-height) + var(--space-2xl)) 0 var(--space-2xl);
        }
        .bb-hero__inner {
          display: flex;
          gap: var(--space-2xl);
          align-items: flex-start;
          flex-wrap: wrap;
        }
        .bb-avatar-wrap {
          flex-shrink: 0;
          width: 160px;
          height: 160px;
          border-radius: 50%;
          overflow: hidden;
          border: 3px solid var(--border);
          background: var(--accent-soft);
          position: relative;
        }
        .bb-avatar {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .bb-avatar-fallback {
          display: none;
          width: 100%;
          height: 100%;
          align-items: center;
          justify-content: center;
          font-family: var(--font-heading);
          font-weight: 700;
          font-size: 2.2rem;
          color: var(--accent);
        }
        .bb-hero__text { flex: 1; min-width: 280px; }
        .bb-eyebrow { display: block; margin-bottom: var(--space-md); }
        .bb-name {
          font-family: var(--font-heading);
          font-size: clamp(2rem, 5vw, 3.2rem);
          font-weight: 700;
          line-height: 1.15;
          color: var(--text);
          margin-bottom: var(--space-sm);
        }
        .bb-role {
          font-size: 0.85rem;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          margin-bottom: var(--space-lg);
        }
        .bb-bio {
          font-size: 1rem;
          line-height: 1.75;
          color: var(--text-muted);
          max-width: 580px;
          margin-bottom: var(--space-xl);
        }
        .bb-hero-links {
          display: flex;
          align-items: center;
          gap: var(--space-md);
          flex-wrap: wrap;
        }
        .bb-hero-links .btn {
          display: flex;
          align-items: center;
          gap: 7px;
        }
        .bb-location {
          display: flex;
          align-items: center;
          gap: 5px;
          font-size: 0.85rem;
        }

        /* ── Skills ── */
        .bb-skills-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
          gap: var(--space-lg);
        }
        .bb-skill-group { padding: var(--space-xl); }
        .bb-skill-group__title {
          font-family: var(--font-heading);
          font-size: 0.9rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          margin-bottom: var(--space-md);
        }
        .bb-skill-tags {
          display: flex;
          flex-wrap: wrap;
          gap: var(--space-sm);
        }
        .bb-skill-tag {
          font-size: 0.8rem;
          background: var(--surface);
          border: 1px solid var(--border);
        }

        /* ── Projects ── */
        .bb-projects-list {
          display: flex;
          flex-direction: column;
          gap: var(--space-lg);
        }
        .bb-project-card { padding: var(--space-xl); }
        .bb-project-card__top {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: var(--space-md);
          margin-bottom: var(--space-md);
          flex-wrap: wrap;
        }
        .bb-project-name {
          font-family: var(--font-heading);
          font-size: 1.1rem;
          font-weight: 700;
          margin-bottom: 4px;
        }
        .bb-project-tagline {
          font-size: 0.85rem;
          text-transform: uppercase;
          letter-spacing: 0.07em;
        }
        .bb-project-link {
          font-size: 0.85rem;
          font-weight: 600;
          color: var(--accent);
          white-space: nowrap;
          transition: opacity 0.2s;
        }
        .bb-project-link:hover { opacity: 0.7; }
        .bb-project-desc {
          font-size: 0.9rem;
          line-height: 1.65;
          margin-bottom: var(--space-md);
        }
        .bb-project-stack {
          display: flex;
          flex-wrap: wrap;
          gap: var(--space-xs);
        }
        .bb-stack-badge {
          font-size: 0.78rem;
          border-color: var(--badge-accent, var(--border));
          color: var(--badge-accent, var(--accent));
          background: transparent;
          opacity: 0.85;
        }

        /* ── Education ── */
        .bb-edu-list {
          display: flex;
          flex-direction: column;
          gap: var(--space-md);
          max-width: 720px;
          margin: 0 auto;
        }
        .bb-edu-item {
          display: flex;
          gap: var(--space-xl);
          padding: var(--space-lg) var(--space-xl);
        }
        .bb-edu-item__left {
          flex-shrink: 0;
          width: 100px;
          padding-top: 3px;
        }
        .bb-edu-period {
          font-size: 0.78rem;
          font-family: monospace;
          letter-spacing: 0.05em;
        }
        .bb-edu-degree {
          font-family: var(--font-heading);
          font-size: 1rem;
          font-weight: 600;
          color: var(--text);
          margin-bottom: 4px;
        }
        .bb-edu-school {
          font-size: 0.85rem;
          font-weight: 500;
          margin-bottom: var(--space-sm);
        }
        .bb-edu-detail {
          font-size: 0.85rem;
          line-height: 1.6;
        }

        /* ── CTA ── */
        .bb-cta { padding-bottom: var(--space-3xl); }
        .bb-cta-card {
          text-align: center;
          padding: var(--space-2xl) var(--space-xl);
          max-width: 600px;
          margin: 0 auto;
          background: linear-gradient(135deg, var(--surface) 0%, var(--accent-soft) 100%);
          border-color: var(--border);
        }
        .bb-cta-title {
          font-family: var(--font-heading);
          font-size: clamp(1.3rem, 3vw, 1.8rem);
          margin-bottom: var(--space-sm);
        }
        .bb-cta-sub {
          font-size: 0.95rem;
          line-height: 1.6;
          max-width: 460px;
          margin: 0 auto var(--space-xl);
        }
        .bb-cta-links {
          display: flex;
          justify-content: center;
          gap: var(--space-md);
          flex-wrap: wrap;
        }

        /* ── Responsive ── */
        @media (max-width: 600px) {
          .bb-hero__inner { flex-direction: column; align-items: center; text-align: center; }
          .bb-hero-links { justify-content: center; }
          .bb-avatar-wrap { width: 120px; height: 120px; }
          .bb-edu-item { flex-direction: column; gap: var(--space-sm); }
          .bb-edu-item__left { width: auto; }
        }
      `}</style>
    </>
  );
}
